import { z } from 'zod';
import { IsoDateTime } from './common.js';

export const AuditActionSchema = z.enum([
  'AUTH_LOGIN', 'AUTH_LOGOUT',
  'PATIENT_SEARCH', 'PATIENT_VIEW', 'PATIENT_REGISTER', 'PATIENT_UPDATE',
  'CONTEXT_SET', 'CONTEXT_CLEAR', 'CONTEXT_SWITCH',
  'RECORD_VIEW', 'RECORD_CREATE', 'RECORD_UPDATE',
  'ORDER_DRAFT', 'ORDER_CONFIRM', 'ORDER_CANCEL',
  'PRESCRIPTION_DRAFT', 'PRESCRIPTION_CONFIRM', 'PRESCRIPTION_CANCEL',
  'APPOINTMENT_DRAFT', 'APPOINTMENT_BOOK', 'APPOINTMENT_CANCEL', 'APPOINTMENT_RESCHEDULE', 'APPOINTMENT_CHECKIN', 'APPOINTMENT_NOSHOW',
  'ENCOUNTER_CREATE', 'ENCOUNTER_UPDATE', 'ENCOUNTER_COMPLETE',
  'NOTE_DRAFT', 'NOTE_SAVE', 'NOTE_SIGN',
  'CDS_EVALUATE', 'CDS_OVERRIDE',
  'PENDING_REJECT', 'PENDING_INVALIDATE',
  'VOICE_SESSION_START', 'VOICE_SESSION_END', 'VOICE_COMMAND', 'TTS_SYNTHESIZE',
  'LLM_INVOKE',
  'TENANT_PROVISION', 'TENANT_UPDATE', 'USER_CREATE', 'USER_UPDATE',
  'DOCUMENT_UPLOAD', 'DOCUMENT_VIEW',
  'ACCESS_DENIED',
]);
export type AuditAction = z.infer<typeof AuditActionSchema>;

export const AuditEventSchema = z.object({
  tenantId: z.string(),
  auditId: z.string(),
  at: IsoDateTime,
  action: AuditActionSchema,
  actorUserId: z.string(),
  actorRoles: z.array(z.string()),
  sessionId: z.string().optional(),
  patientId: z.string().optional(),
  resourceType: z.string().optional(),
  resourceId: z.string().optional(),
  outcome: z.enum(['SUCCESS', 'FAILURE', 'DENIED']),
  /** Never contains PHI beyond identifiers; free text is limited to command text the user approved. */
  detail: z.record(z.string(), z.unknown()).optional(),
  requestId: z.string().optional(),
  sourceIp: z.string().optional(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const NotificationSchema = z.object({
  tenantId: z.string(),
  notificationId: z.string(),
  recipientUserId: z.string().optional(),
  recipientPatientId: z.string().optional(),
  channel: z.enum(['IN_APP', 'SMS', 'EMAIL', 'PUSH']),
  template: z.string(),
  params: z.record(z.string(), z.string()).default({}),
  status: z.enum(['QUEUED', 'SENT', 'FAILED', 'READ']),
  scheduledFor: IsoDateTime.optional(),
  sentAt: IsoDateTime.optional(),
  createdAt: IsoDateTime,
});
export type Notification = z.infer<typeof NotificationSchema>;

/** Usage metering for tenant billing. One row per (tenant, metric, period bucket). */
export const UsageMetricSchema = z.object({
  tenantId: z.string(),
  metric: z.enum([
    'LLM_INPUT_TOKENS', 'LLM_OUTPUT_TOKENS', 'LLM_INVOCATIONS',
    'TRANSCRIBE_STREAM_MS', 'POLLY_CHARACTERS',
    'API_REQUESTS', 'ACTIVE_USERS', 'ACTIVE_PATIENTS', 'STORAGE_BYTES',
  ]),
  period: z.string().describe('YYYY-MM-DD for daily buckets'),
  value: z.number(),
  updatedAt: IsoDateTime,
});
export type UsageMetric = z.infer<typeof UsageMetricSchema>;
